import React, { useRef, useEffect } from 'react';
import { User, Bot, Loader2, Copy, Check } from 'lucide-react';
import { ChatMessage } from '../types';

interface ConversationDisplayProps {
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming?: boolean;
  streamingContent?: string;
}

const MessageContent: React.FC<{ content: string; isUser: boolean }> = ({ content, isUser }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple markdown-like formatting
  const formatContent = (text: string) => {
    // Split by newlines and process
    const lines = text.split('\n');

    return lines.map((line, i) => {
      // Headers
      if (line.startsWith('### ')) {
        return <h4 key={i} className="font-semibold text-base mt-3 mb-1">{line.slice(4)}</h4>;
      }
      if (line.startsWith('## ')) {
        return <h3 key={i} className="font-semibold text-lg mt-4 mb-2">{line.slice(3)}</h3>;
      }
      if (line.startsWith('# ')) {
        return <h2 key={i} className="font-bold text-xl mt-4 mb-2">{line.slice(2)}</h2>;
      }

      // Bullet points
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return (
          <div key={i} className="flex gap-2 ml-2">
            <span className="text-gray-400">-</span>
            <span>{formatInlineText(line.slice(2))}</span>
          </div>
        );
      }

      // Numbered lists
      const numberedMatch = line.match(/^(\d+)\. /);
      if (numberedMatch) {
        return (
          <div key={i} className="flex gap-2 ml-2">
            <span className="text-gray-400 min-w-[1.5rem]">{numberedMatch[1]}.</span>
            <span>{formatInlineText(line.slice(numberedMatch[0].length))}</span>
          </div>
        );
      }

      // Empty lines
      if (line.trim() === '') {
        return <div key={i} className="h-2" />;
      }

      // Regular paragraphs
      return <p key={i} className="my-1">{formatInlineText(line)}</p>;
    });
  };

  const formatInlineText = (text: string) => {
    // Bold text **text**
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      // Inline code `code`
      const codeParts = part.split(/(`[^`]+`)/g);
      return codeParts.map((codePart, j) => {
        if (codePart.startsWith('`') && codePart.endsWith('`')) {
          return (
            <code key={`${i}-${j}`} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
              {codePart.slice(1, -1)}
            </code>
          );
        }
        return codePart;
      });
    });
  };

  if (isUser) {
    return <p className="whitespace-pre-wrap">{content}</p>;
  }

  return (
    <div className="relative group">
      <div className="prose prose-sm max-w-none dark:prose-invert">
        {formatContent(content)}
      </div>
      <button
        onClick={handleCopy}
        className="absolute top-0 right-0 p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy to clipboard"
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-500" />
        ) : (
          <Copy className="w-4 h-4 text-gray-500" />
        )}
      </button>
    </div>
  );
};

export const ConversationDisplay: React.FC<ConversationDisplayProps> = ({
  messages,
  isLoading,
  isStreaming = false,
  streamingContent = ''
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages or streaming content
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, isStreaming, streamingContent]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
          <Bot className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>
        <p className="text-lg font-medium text-gray-900 dark:text-white">Legal AI Assistant</p>
        <p className="text-sm mt-1 text-center max-w-md">
          Ask questions about contracts, paste a clause for analysis, or use quick actions to get started.
        </p>
        <div className="mt-6 grid grid-cols-2 gap-2 text-sm">
          <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            "What are the risks in this clause?"
          </div>
          <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            "Make this GDPR compliant"
          </div>
          <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            "Simplify this language"
          </div>
          <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            "Strengthen my position"
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages
        .filter(m => m.role !== 'system')
        .map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role === 'assistant' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            )}

            <div
              className={`max-w-[85%] rounded-xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-gray-900 dark:text-white'
              }`}
            >
              <MessageContent content={message.content} isUser={message.role === 'user'} />
            </div>

            {message.role === 'user' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </div>
            )}
          </div>
        ))}

      {/* Streaming content display */}
      {isStreaming && streamingContent && (
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="max-w-[85%] bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl px-4 py-3 text-gray-900 dark:text-white">
            <MessageContent content={streamingContent} isUser={false} />
            <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Generating...</span>
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator (when not streaming) */}
      {isLoading && !isStreaming && (
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Analyzing...</span>
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};

export default ConversationDisplay;
